<template>
  <div class="pqr">
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <template v-else-if="stage === 'generating'">
      <div class="text-center py-8" data-testid="pq-generating">
        <v-progress-circular indeterminate color="primary" class="mb-4" />
        <div class="text-h6">Writing provider questions…</div>
        <div v-if="stuck" class="mt-4">
          <div class="text-body-2 text-medium-emphasis mb-3">This is taking longer than usual.</div>
          <v-btn color="primary" :disabled="busy" :loading="going === 'retry'" @click="retry">Try again</v-btn>
          <v-btn v-if="inSetup" variant="text" class="ml-2" :disabled="busy" :loading="going === 'skip'" @click="skip">
            Skip for now
          </v-btn>
        </div>
      </div>
    </template>

    <template v-else-if="stage === 'failed' || timedOut">
      <div class="text-center py-8" data-testid="pq-failed">
        <v-icon color="error" size="48" class="mb-3">mdi-alert-circle-outline</v-icon>
        <div class="text-h6 mb-4">We couldn't write provider questions.</div>
        <v-btn color="primary" :disabled="busy" :loading="going === 'retry'" @click="retry">Try again</v-btn>
        <v-btn v-if="inSetup" variant="text" class="ml-2" :disabled="busy" :loading="going === 'skip'" @click="skip">
          Skip for now
        </v-btn>
      </div>
    </template>

    <template v-else>
      <div class="text-center mb-6">
        <v-icon color="primary" size="64" class="mb-4">mdi-pencil-outline</v-icon>
        <div class="text-h5 mb-2">Review provider questions</div>
        <div class="text-body-1 text-medium-emphasis">
          Each service line question is rewritten to ask about doctors. Every provider on this audit is checked with these questions. Edit the provider version if you like.
        </div>
      </div>

      <v-alert v-if="notice" type="info" variant="tonal" class="mb-4">{{ notice }}</v-alert>

      <v-card v-for="persona in personas" :key="persona.id" variant="outlined" class="mb-4 pa-4">
        <div class="text-h6 font-weight-bold mb-4 pb-2 pqr-persona">{{ persona.name }}</div>
        <v-row v-for="(q, index) in persona.questions" :key="q.id" class="mb-2" data-testid="pq-row">
          <v-col cols="12" md="5">
            <div class="text-caption text-medium-emphasis mb-1">Service line question #{{ index + 1 }}</div>
            <div class="text-body-1 pqr-source">{{ q.source_text }}</div>
            <a v-if="inSetup" href="#" class="text-body-2" data-testid="pq-edit-step-1" @click.prevent="editStep1">
              Edit on step 1
            </a>
          </v-col>
          <v-col cols="12" md="7">
            <div class="d-flex align-center mb-1" style="min-height: 20px;">
              <span class="text-caption text-medium-emphasis">Provider version</span>
              <v-chip v-if="isEdited(q)" size="x-small" color="primary" variant="tonal" class="ml-2" data-testid="pq-edited">
                edited
              </v-chip>
              <v-spacer />
              <a v-if="isEdited(q)" href="#" class="text-body-2" data-testid="pq-reset" @click.prevent="reset(q)">
                Reset to our suggestion
              </a>
            </div>
            <v-text-field
              v-model="texts[q.id]"
              variant="outlined"
              density="comfortable"
              hide-details
              @blur="save(q)"
            />
          </v-col>
        </v-row>
      </v-card>

      <v-alert v-if="error" type="error" variant="tonal" class="mb-4">{{ error }}</v-alert>

      <div class="d-flex justify-center mt-6">
        <v-btn color="primary" size="large" :disabled="busy || hasBlank" :loading="going === 'confirm'" @click="confirm">
          Continue
          <v-icon class="ml-2">mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </template>

    <v-alert v-if="error && (stage === 'failed' || stage === 'generating' || timedOut)" type="error" variant="tonal" class="mt-4">
      {{ error }}
    </v-alert>
  </div>
</template>

<script>
import {
  getProviderQuestions, updateProviderQuestions, resetProviderQuestion,
  confirmProviderQuestions, retryProviderQuestions, skipProviderQuestions
} from './providersApi'
import { errorMessage, isStale, QUESTIONS_CHANGED } from './providerErrors'

const POLL_MS = 5000
const POLL_LIMIT_MS = 16 * 60 * 1000

export default {
  name: 'ProviderQuestionReview',
  props: {
    reviewEntityId: { type: [Number, String], required: true },
    inSetup: { type: Boolean, default: false }
  },
  emits: ['edit-step-1', 'confirmed', 'skipped'],
  data() {
    return {
      loading: true, stage: '', stuck: false, personas: [], texts: {},
      going: '', pendingSave: null, loaded: {}, error: '', notice: '', timedOut: false, timer: null, pollStart: 0
    }
  },
  computed: {
    busy() { return !!this.going },
    hasBlank() { return Object.values(this.texts).some(t => !String(t ?? '').trim()) }
  },
  created() { this.load() },
  beforeUnmount() { this.stopPolling() },
  methods: {
    isEdited(q) { return !!q.edited },
    apply(data, { fresh = false } = {}) {
      this.stage = data.stage
      this.stuck = !!data.stuck
      this.personas = data.personas || []
      const texts = {}
      this.personas.forEach(p => (p.questions || []).forEach(q => { texts[q.id] = q.text }))
      // Keep any field the user has changed since the last load, so a refresh never eats their typing.
      const prev = this.loaded || {}
      const kept = {}
      Object.keys(texts).forEach(id => {
        kept[id] = !fresh && id in this.texts && id in prev && this.texts[id] !== prev[id] ? this.texts[id] : texts[id]
      })
      this.loaded = { ...texts }
      this.texts = kept
    },
    async load({ stale = false, quiet = false } = {}) {
      try {
        const res = await getProviderQuestions(this.reviewEntityId)
        this.apply(res.provider_questions, { fresh: stale })
        if (stale) this.notice = QUESTIONS_CHANGED
        this.syncPolling()
      } catch (e) {
        if (!quiet) this.error = errorMessage(e, "We couldn't load the provider questions. Try again.")
      } finally {
        this.loading = false
      }
    },
    syncPolling() {
      if (this.stage === 'generating' && !this.timedOut) {
        if (!this.timer) {
          if (!this.pollStart) this.pollStart = Date.now()
          this.timer = setInterval(this.poll, POLL_MS)
        }
      } else {
        this.stopPolling()
      }
    },
    stopPolling() {
      if (this.timer) clearInterval(this.timer)
      this.timer = null
    },
    async poll() {
      if (Date.now() - this.pollStart > POLL_LIMIT_MS) {
        this.timedOut = true
        this.stopPolling()
        return
      }
      await this.load({ quiet: true })
    },
    async run(kind, fn) {
      if (this.busy) return
      this.going = kind
      this.error = ''
      try {
        await fn()
      } catch (e) {
        if (isStale(e)) await this.load({ stale: true })
        else this.error = errorMessage(e, "We couldn't save the questions. Check them and try again.")
      } finally {
        this.going = ''
      }
    },
    // Saves one edited question as soon as the field loses focus, so the edited tag and the reset link appear.
    save(q) {
      const text = String(this.texts[q.id] ?? '').trim()
      if (this.busy || !text || text === q.text) return
      // Not through run(): saving must never disable Continue, or the click that caused this blur is lost.
      this.pendingSave = (async () => {
        try {
          await updateProviderQuestions(this.reviewEntityId, [{ id: q.id, text }])
          this.notice = ''
          await this.load()
        } catch (e) {
          if (isStale(e)) await this.load({ stale: true })
          else this.error = errorMessage(e, "We couldn't save that question. Check it and try again.")
        }
      })()
      return this.pendingSave
    },
    changedQuestions() {
      const out = []
      this.personas.forEach(p => (p.questions || []).forEach(q => {
        const text = String(this.texts[q.id] ?? '').trim()
        if (text !== q.text) out.push({ id: q.id, text })
      }))
      return out
    },
    confirm() {
      return this.run('confirm', async () => {
        await this.pendingSave
        const changed = this.changedQuestions()
        if (changed.length) await updateProviderQuestions(this.reviewEntityId, changed)
        await confirmProviderQuestions(this.reviewEntityId)
        this.$emit('confirmed')
      })
    },
    reset(q) {
      return this.run('reset', async () => {
        await resetProviderQuestion(this.reviewEntityId, q.id)
        this.texts[q.id] = this.loaded[q.id] // the reset text arrives with the reload
        this.notice = ''
        await this.load()
      })
    },
    retry() {
      return this.run('retry', async () => {
        await retryProviderQuestions(this.reviewEntityId)
        this.timedOut = false
        this.pollStart = 0
        await this.load()
      })
    },
    skip() {
      return this.run('skip', async () => {
        await skipProviderQuestions(this.reviewEntityId)
        this.stopPolling()
        this.$emit('skipped')
      })
    },
    async editStep1() {
      if (this.busy) return
      await this.pendingSave
      this.$emit('edit-step-1')
    }
  }
}
</script>

<style scoped>
.pqr-persona { border-bottom: 2px solid #e0e0e0; }
.pqr-source { white-space: pre-wrap; }
</style>
